import SortOptionsIcon from '@atlaskit/icon-lab/core/sort-options';
import DragHandleVerticalIcon from '@atlaskit/icon/core/drag-handle-vertical';
import ProjectStatusIcon from '@atlaskit/icon/core/project-status';
import SortAscendingIcon from '@atlaskit/icon/core/sort-ascending';
import SortDescendingIcon from '@atlaskit/icon/core/sort-descending';
import { Inline, Text } from '@atlaskit/primitives';
import { type MenuItem } from '@databases/contextual-menu/ContextualMenu';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import {
  getLabelMessage,
  getSortCategory,
  dataModelToSortByDirection,
  sortByDirectionToDataModel,
  type CustomOrderVariant,
  type SortByDirection
} from '@databases/utils/database/view/sort-direction';
import React, { type ComponentType } from 'react';
import { type IntlShape } from 'react-intl-next';

const getCustomOrderVariant = (field: DatabaseField): CustomOrderVariant | undefined => {
  if (
    field.type === DatabaseFieldType.JIRA_ISSUE_DETAILS &&
    field.settings.jiraField === 'status'
  ) {
    return 'statusOrder';
  }

  if (field.type === DatabaseFieldType.SELECT) {
    return 'default';
  }

  return undefined;
};

const getSortDirectionIcon = (
  direction: SortByDirection,
  customOrderVariant?: CustomOrderVariant
): ComponentType<{ label: string }> => {
  switch (direction) {
    case 'customOrder':
      return customOrderVariant === 'statusOrder'
        ? ProjectStatusIcon
        : DragHandleVerticalIcon;
    case 'reverseCustomOrder':
      return SortOptionsIcon;
    case 'descending':
      return SortAscendingIcon;
    case 'ascending':
    default:
      return SortDescendingIcon;
  }
};

const SortFieldSubmenuItemContent = ({
  Icon,
  label
}: {
  Icon: ComponentType<{ label: string }>;
  label: string;
}) => (
  <Inline space="space.150" alignBlock="center">
    <Icon label="" />
    <Text color="inherit">{label}</Text>
  </Inline>
);

export const createFieldSortSubmenuItems = ({
  field,
  fieldId,
  formatMessage,
  selectedDirection,
  onSortChange
}: {
  field: DatabaseField;
  fieldId: string;
  formatMessage: IntlShape['formatMessage'];
  selectedDirection?: SortByDirection;
  onSortChange: (sort: {
    fieldId: string;
    ascending: boolean;
    customOrder?: boolean;
  }) => void;
}): MenuItem[] => {
  const sortCategory = getSortCategory(field);
  const customOrderVariant = getCustomOrderVariant(field);
  const directions: SortByDirection[] = customOrderVariant
    ? ['customOrder', 'reverseCustomOrder', 'ascending', 'descending']
    : ['ascending', 'descending'];

  const directionKeys: Record<SortByDirection, string> = {
    customOrder: 'sort_field_custom_order',
    reverseCustomOrder: 'sort_field_reverse_custom_order',
    ascending: 'sort_field_increasing',
    descending: 'sort_field_decreasing'
  };

  return directions.map((direction) => {
    const Icon = getSortDirectionIcon(direction, customOrderVariant);
    const { ascending, customOrder } = sortByDirectionToDataModel(direction);
    const label = formatMessage(
      getLabelMessage({
        direction,
        sortCategory,
        customOrderVariant
      })
    );

    return {
      key: directionKeys[direction],
      name: label,
      content: <SortFieldSubmenuItemContent Icon={Icon} label={label} />,
      onClick: () =>
        onSortChange({
          fieldId,
          ascending,
          customOrder
        }),
      elemBefore: undefined,
      isActive: selectedDirection === direction
    };
  });
};

export const getSelectedSortDirection = ({
  ascending,
  customOrder
}: {
  ascending: boolean;
  customOrder?: boolean;
}): SortByDirection => dataModelToSortByDirection(ascending, customOrder);
